// MessageList — message stream. Own vs others bubbles (with sender avatars),
// system pills, command-result cards, sign-off cards, inline image + voice-note
// players, soft-deleted placeholder, and @mention highlighting.

import { inOneHour, tomorrowAt, nextMondayAt, whenLabel } from "../lib/when.js";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import AdReportCard from "./AdReportCard.jsx";
import { createPortal } from "react-dom";
import { MessagesApi } from "../hooks/useApi.js";
import { PollCard } from "./Poll.jsx";
import { IdeaCard } from "./Idea.jsx";
import SignoffCard from "./SignoffCard.jsx";
import MeetingCard from "./MeetingCard.jsx";
import DeliverableCard from "./DeliverableCard.jsx";
import StoryboardCard from "./StoryboardCard.jsx";
import Icon from "./Icon.jsx";
import Welcome from "./Welcome.jsx";
import Avatar from "./Avatar.jsx";
import NeuralCore from "./NeuralCore.jsx";
import SpookyEventCard from "./SpookyEventCard.jsx";
import DeepSpaceEventCard from "./DeepSpaceEventCard.jsx";
import SummaryCard from "./SummaryCard.jsx";
import DeepSpaceSummaryCard from "./DeepSpaceSummaryCard.jsx";
import { ticketType, ticketLabel, ticketByline } from "../lib/ticketType.js";
import { EMOJI_CATEGORIES, searchEmoji } from "../emoji.js";

// Cyberpunk uses an animated "neural core" glyph for status updates in place of the
// static refresh arrow. Read the persisted theme attribute at render.
const isCyber = () => typeof document !== "undefined" && document.documentElement.dataset.theme === "cyberpunk";
const isSpooky = () => typeof document !== "undefined" && document.documentElement.dataset.theme === "spooky";
const isDeep = () => typeof document !== "undefined" && document.documentElement.dataset.theme === "deep";

// A status-update card is a System message tagged kind:"status". Used by the per-user
// "hide status updates" preference to strip the wall of status cards from the stream.
const isStatusMsg = (m) => {
  if (m.messageType !== 4 /* System */) return false;
  try { return JSON.parse(m.metadataJson || "{}").kind === "status"; } catch { return false; }
};
import { visibleCommands } from "../commands.js";
import LinkEmbed, { embeddableUrls } from "./LinkEmbed.jsx";
import { useAuth } from "../auth/AuthProvider.jsx";

// Mirrors TmcChat.Models.MessageType
const T = { Text: 0, Audio: 1, Image: 2, Video: 3, System: 4, CommandResult: 5, Signoff: 6, Deliverable: 7, Storyboard: 8, Meeting: 9, File: 10 };

// A generic file attachment (PDF, HTML doc, zip, …). The filename rides in the message content.
// HTML docs get a "Preview" that renders them in a locked-down sandboxed iframe (?inline=true makes
// the server send a `sandbox` CSP so the doc runs as an opaque origin — no access to our cookies /
// origin), plus a Download. Everything else is just an open/download chip.
// Live progress toward the server's ETA for meeting-notes generation (etaSeconds/startedAt in
// the pending card's metadata — sized from how much audio was recorded). Fills to 90% at the
// ETA then crawls, never claiming done: the server edits this card into the real notes when it
// finishes. Old cards without an ETA keep the plain pulsing line.
function NotesProgress({ etaSeconds, startedAt }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 2000);
    return () => clearInterval(t);
  }, []);
  const start = typeof startedAt === "number" ? startedAt : new Date(startedAt).getTime();
  const elapsed = Math.max(0, (now - start) / 1000);
  const frac = elapsed / Math.max(etaSeconds, 1);
  const pct = frac <= 1 ? frac * 90 : Math.min(97, 90 + (frac - 1) * 10);
  const remaining = etaSeconds - elapsed;
  const label = remaining > 90
    ? `Transcribing & summarizing — about ${Math.round(remaining / 60)} min left…`
    : remaining > 0
      ? "Transcribing & summarizing — less than a minute left…"
      : "Almost there — taking a bit longer than usual…";
  return (
    <div className="notes-progress">
      <div className="notes-progress__track">
        <div className="notes-progress__fill" style={{ width: `${pct.toFixed(1)}%` }} />
      </div>
      <span className="notes-progress__label">{label}</span>
    </div>
  );
}

// An audio file sent as an attachment (mp3, m4a, …) plays inline with the voice-note player.
const isAudioName = (name) => /\.(mp3|m4a|aac|wav|ogg|oga|opus|flac)$/i.test((name || "").trim());

function FileCard({ url, name }) {
  const [full, setFull] = useState(false); // full-screen HTML viewer overlay
  const fname = (name || "file").trim();
  const ext = fname.split(".").pop()?.toLowerCase();
  const isHtml = ext === "html" || ext === "htm";
  const inlineSrc = `${url}${url.includes("?") ? "&" : "?"}inline=true`;

  // While the full-screen viewer is open: Esc closes it and the page behind it doesn't scroll.
  useEffect(() => {
    if (!full) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setFull(false); };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prevOverflow; };
  }, [full]);

  return (
    <div className="msg__file">
      <div className="msg__file-head">
        <span className="msg__file-icon"><Icon name="document" /></span>
        <span className="msg__file-name" title={fname}>{fname || "Document"}</span>
      </div>
      <div className="msg__file-actions">
        {isHtml && (
          <button type="button" className="msg__file-btn" onClick={() => setFull(true)}>Preview</button>
        )}
        <a className="msg__file-btn" href={`${url}${url.includes("?") ? "&" : "?"}name=${encodeURIComponent(fname)}`} target="_blank" rel="noreferrer noopener" download={fname}>Download</a>
      </div>
      {isHtml && full && createPortal(
        // Portalled to <body> so it fills the viewport on desktop AND mobile without the chat's
        // scroll containers / transforms clipping it. Same sandboxed inline src as the inline preview.
        <div className="html-fs" role="dialog" aria-modal="true" aria-label={fname}>
          <div className="html-fs__bar">
            <span className="html-fs__title" title={fname}>{fname || "Document"}</span>
            <button type="button" className="html-fs__close" onClick={() => setFull(false)} aria-label="Close full screen">
              <Icon name="close" />
            </button>
          </div>
          <iframe className="html-fs__frame" title={fname} src={inlineSrc}
                  sandbox="allow-scripts allow-popups allow-forms" referrerPolicy="no-referrer" />
        </div>,
        document.body,
      )}
    </div>
  );
}

// The full-emoji reaction picker opened from the message menu / long-press sheet's "More emoji"
// button (both were previously dead). Portalled to <body> so it isn't clipped, uses onClick (works on
// desktop AND mobile touch — the composer's picker uses onMouseDown which mobile can't rely on), and
// closes on pick / backdrop tap / Esc. Reuses the same emoji set + search as the composer picker.
function ReactionPicker({ onPick, onClose }) {
  const [q, setQ] = useState("");
  const results = q.trim() ? searchEmoji(q) : null;
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return createPortal(
    <div className="reactpick__overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label="Pick a reaction">
      <div className="reactpick" onClick={(e) => e.stopPropagation()}>
        <div className="reactpick__search">
          <Icon name="search" className="ic--sm" />
          <input autoFocus value={q} placeholder="Find an emoji" onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="reactpick__scroll">
          {results ? (
            results.length
              ? <div className="reactpick__grid">{results.map((e, i) => (
                  <button type="button" key={i} className="reactpick__emoji" onClick={() => onPick(e)}>{e}</button>
                ))}</div>
              : <div className="reactpick__empty">No emoji found</div>
          ) : EMOJI_CATEGORIES.map((c) => (
            <div key={c.id} className="reactpick__section">
              <div className="reactpick__label">{c.label}</div>
              <div className="reactpick__grid">
                {c.emojis.map(([ch], i) => (
                  <button type="button" key={i} className="reactpick__emoji" title={ch} onClick={() => onPick(ch)}>{ch}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// Day grouping for date separators between messages.
const dayKey = (iso) => { const d = new Date(iso); return isNaN(d) ? "" : `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`; };
function dayLabel(iso) {
  const d = new Date(iso); if (isNaN(d)) return "";
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  const diff = Math.round((startOf(new Date()) - startOf(d)) / 86400000);
  if (diff <= 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff < 7) return d.toLocaleDateString(undefined, { weekday: "short" });
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

// "14 Oct" from "YYYY-MM-DD" (leave end dates).
function leaveLabel(ymd) {
  const d = new Date(`${ymd}T00:00:00`);
  return Number.isNaN(d.getTime()) ? ymd : d.toLocaleDateString([], { day: "numeric", month: "short" });
}

function timeLabel(iso) {
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}
function statusChip(name) {
  const s = (name || "").toLowerCase();
  if (s.includes("complete") || s.includes("released") || s.includes("done")) return "done";
  if (s.includes("progress") || s.includes("testing") || s.includes("qa") || s.includes("review")) return "partial";
  if (s.includes("await") || s.includes("hold") || s.includes("brief") || s.includes("pending")) return "pending";
  return "open";
}
function durLabel(s) {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60), sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
}

// Build card — shared by the live build webhook (System) and the private /test-apps result
// (CommandResult). Each platform block reflects its most-recent build; tasks are the "what to test" list.
function BuildCard({ m, meta, onCardOpen }) {
  const a = meta.android || {}, ios = meta.ios || {}, hw = meta.huawei || {};
  const tasks = meta.tasks || [];
  return (
    <div className="build-card">
      <div className="build-card__head">
        <span className="build-card__icon">📱</span>
        <div className="build-card__ht">
          <strong className="build-card__title">{meta.productName || "New test build"}</strong>
          <span className="build-card__sub">New build ready to test{meta.track ? ` · ${meta.track}` : ""}</span>
        </div>
        <span className="card-time">{timeLabel(m.createdAt)}</span>
      </div>

      <div className="build-card__versions">
        {a.version ? <span className="build-ver">🤖 Android <b>v{a.version}</b>{a.build ? ` (${a.build})` : ""}</span> : null}
        {ios.available ? <span className="build-ver">🍎 iOS <b>v{ios.version}</b>{ios.build ? ` (${ios.build})` : ""}</span> : null}
        {hw.available ? <span className="build-ver">📦 Huawei <b>v{hw.version}</b>{hw.build ? ` (${hw.build})` : ""}</span> : null}
      </div>

      {tasks.length > 0 && (
        <div className="build-card__test">
          <span className="build-card__label">What to test ({tasks.length})</span>
          <ul className="build-tasks">
            {tasks.map((t, idx) => {
              const openable = !!(t.issueId || t.id);
              return (
                <li key={idx}>
                  <button
                    type="button"
                    className="build-task"
                    disabled={!openable}
                    onClick={() => openable && onCardOpen?.({ kind: "ticket", issueId: t.issueId, taskId: t.id, id: t.issueId || t.id, title: t.name, isBug: !!t.isBug, by: t.assignedTo })}
                  >
                    <span className="build-task__type"><Icon name={t.isBug ? "bug" : "feature"} className="ic--sm" /></span>
                    <span className="build-task__name">{t.name}</span>
                    {t.priority ? <span className={`build-task__pri build-task__pri--${(t.priority || "").toLowerCase()}`}>{t.priority}</span> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="build-card__links">
        {a.testingProgramLink ? <a className="build-link build-link--primary" href={a.testingProgramLink} target="_blank" rel="noreferrer">🚀 Join testing program</a> : null}
        {a.buildLink ? <a className="build-link" href={a.buildLink} target="_blank" rel="noreferrer">📦 Direct build link</a> : null}
      </div>
      {(ios.available || hw.available) && (
        <p className="build-card__note">
          {ios.available ? `🍎 iOS: open TestFlight for v${ios.version}. ` : ""}
          {hw.available ? `📦 Huawei: open AppGallery for v${hw.version}.` : ""}
        </p>
      )}
    </div>
  );
}

// Minimal, safe markdown for command/system text: escape, then **bold** and `code`.
function mdLite(text) {
  const esc = (text ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return esc
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*\n]+?)\*/g, "<em>$1</em>")
    .replace(/`([^`]+?)`/g, "<code>$1</code>");
}

// Render inline **bold** spans within a line of text (React-safe, no innerHTML).
function inlineBold(text, keyBase) {
  const keyRef = { k: 0 };
  return (text || "").split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((p, i) => {
    const m = /^\*\*([^*]+)\*\*$/.exec(p);
    // Non-bold segments get URL-linkified so links the summary weaves into a point are tappable.
    return m ? <strong key={`${keyBase}-${i}`}>{m[1]}</strong> : <span key={`${keyBase}-${i}`}>{linkify(p, keyRef)}</span>;
  });
}

// Render a markdown-ish summary as section headers + bullet lists + paragraphs.
function SummaryBody({ text }) {
  const blocks = [];
  let bullets = null;
  const flush = () => { if (bullets) { blocks.push(<ul key={`ul-${blocks.length}`} className="summary-card__list">{bullets}</ul>); bullets = null; } };
  (text || "").split(/\r?\n/).forEach((raw, i) => {
    const line = raw.trim();
    if (!line) { flush(); return; }
    const header = /^\*\*(.+?)\*\*:?\s*$/.exec(line);         // a whole-line bold = section header
    if (header) { flush(); blocks.push(<div key={`h-${i}`} className="summary-card__h">{header[1]}</div>); return; }
    const bullet = /^[-*•]\s+(.*)$/.exec(line);               // "- item"
    if (bullet) { (bullets = bullets || []).push(<li key={`li-${i}`}>{inlineBold(bullet[1], i)}</li>); return; }
    flush();
    blocks.push(<p key={`p-${i}`} className="summary-card__p">{inlineBold(line, i)}</p>);
  });
  flush();
  return <>{blocks}</>;
}

// Turn bare http(s) URLs in a plain-text segment into tappable links. Restricted to
// http/https (so "javascript:" can't sneak through) and trailing punctuation is trimmed
// off the URL. stopPropagation so tapping a link doesn't also fire the message row's tap.
const URL_RE = /(https?:\/\/[^\s<]+)/g;
function linkify(str, keyRef) {
  if (!str) return [str];
  const nodes = []; let last = 0; let m;
  URL_RE.lastIndex = 0;
  while ((m = URL_RE.exec(str)) !== null) {
    let url = m[0];
    const trail = url.match(/[.,!?;:)\]}'"]+$/);
    if (trail) url = url.slice(0, -trail[0].length);
    if (m.index > last) nodes.push(str.slice(last, m.index));
    nodes.push(
      <a key={`u${keyRef.k++}`} className="msg__link" href={url} target="_blank"
         rel="noreferrer noopener" onClick={(e) => e.stopPropagation()}>{url}</a>
    );
    last = m.index + url.length;
    URL_RE.lastIndex = last;
  }
  if (last < str.length) nodes.push(str.slice(last));
  return nodes;
}

// Highlight "@Name" for mentionable names (longest-first, React-safe — no innerHTML) and
// linkify bare URLs in the plain-text between mentions (mentions themselves are never scanned).
// Renders message text with @name mentions highlighted inline, AND (Dream Team) #channel references
// rendered inline as a tappable chip — mirroring the mention so a "#Channel" reads in the flow of the
// message and a tap opens that channel. channelRefs is the message's {id,name} metadata; a "#name" is
// only linkified when it matches one (so a stray "#" is left as plain text). Names are matched
// longest-first so multi-word channel names ("#Westfert Sales") win over any shorter prefix.
function renderWithMentions(text, names, channelRefs, onChannelOpen) {
  if (!text) return text;
  const keyRef = { k: 0 };
  const chans = (channelRefs || []).filter((c) => c && c.name).sort((a, b) => b.name.length - a.name.length);
  if (!names?.length && !chans.length) return linkify(text, keyRef);
  const sorted = [...(names || [])].sort((a, b) => b.length - a.length);
  const out = []; let rest = text; let key = 0;
  outer: while (rest.length) {
    for (const name of sorted) {
      if (rest.startsWith(`@${name}`)) {
        out.push(<span key={`m${key++}`} className="mention">@{name}</span>);
        rest = rest.slice(name.length + 1);
        continue outer;
      }
    }
    for (const c of chans) {
      if (rest.startsWith(`#${c.name}`)) {
        out.push(
          <button type="button" key={`c${key++}`} className="mention mention--channel"
                  title={`Go to ${c.name}`} onClick={() => onChannelOpen?.(c.id)}>#{c.name}</button>,
        );
        rest = rest.slice(c.name.length + 1);
        continue outer;
      }
    }
    let next = rest.length;
    for (const name of sorted) { const i = rest.indexOf(`@${name}`); if (i > 0) next = Math.min(next, i); }
    for (const c of chans) { const i = rest.indexOf(`#${c.name}`); if (i > 0) next = Math.min(next, i); }
    out.push(...linkify(rest.slice(0, next), keyRef));
    rest = rest.slice(next);
  }
  return out;
}

const BARS = 32;
const barH = (i) => 6 + ((i * 5) % 17); // the idle "waveform" shape

// Our own media (/api/media/…) normally redirects to a GCS signed URL; ?inline=true streams it from
// this origin instead, which Web Audio needs to read the samples for the live spectrum (a
// cross-origin <audio> fed through an AnalyserNode plays silence).
const sameOriginSrc = (src) => (src?.startsWith("/api/media/") ? `${src}${src.includes("?") ? "&" : "?"}inline=true` : null);

function VoicePlayer({ src, mine, title = null, downloadHref = null }) {
  const ref = useRef(null);
  const waveRef = useRef(null);
  const barsRef = useRef([]);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [dur, setDur] = useState(0);
  const [cur, setCur] = useState(0);
  const [viaUs, setViaUs] = useState(true); // falls back to the plain URL if our stream fails
  const localSrc = viaUs ? sameOriginSrc(src) : null;

  // Spectrum analyser, wired up on the first play (browsers only allow audio contexts after a tap).
  const ensureAnalyser = () => {
    const a = ref.current;
    if (!analyserRef.current && localSrc && a) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) {
        try {
          const ctx = new AC();
          const an = ctx.createAnalyser();
          an.fftSize = 128;
          an.smoothingTimeConstant = 0.78;
          ctx.createMediaElementSource(a).connect(an);
          an.connect(ctx.destination);
          audioCtxRef.current = ctx;
          analyserRef.current = an;
        } catch { /* no analyser — the clip still plays, bars stay static */ }
      }
    }
    audioCtxRef.current?.resume?.().catch(() => {});
  };
  useEffect(() => () => { audioCtxRef.current?.close?.().catch(() => {}); }, []);

  // While playing, bounce the bars with the live frequency data (low → high across the row).
  useEffect(() => {
    const an = analyserRef.current;
    const bars = barsRef.current;
    if (!playing || !an) {
      bars.forEach((b, i) => { if (b) b.style.height = `${barH(i)}px`; });
      return undefined;
    }
    const data = new Uint8Array(an.frequencyBinCount);
    const usable = Math.floor(data.length * 0.7); // the top bins are near-empty for speech/music
    let raf = 0;
    const tick = () => {
      an.getByteFrequencyData(data);
      for (let i = 0; i < BARS; i++) {
        const lo = Math.floor(Math.pow(i / BARS, 1.7) * usable);
        const hi = Math.max(lo + 1, Math.floor(Math.pow((i + 1) / BARS, 1.7) * usable));
        let v = 0;
        for (let k = lo; k < hi; k++) v = Math.max(v, data[k]);
        if (bars[i]) bars[i].style.height = `${3 + (v / 255) * 25}px`;
      }
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [playing]);

  // Seek by tapping/dragging anywhere on the waveform → jump to that point in the clip.
  const seekToClientX = (clientX) => {
    const el = waveRef.current, a = ref.current;
    if (!el || !a || !(dur > 0)) return;
    const r = el.getBoundingClientRect();
    const f = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
    a.currentTime = f * dur;
    setCur(f * dur);
  };
  const onScrub = (e) => {
    e.preventDefault();
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* noop */ }
    seekToClientX(e.clientX);
    const move = (ev) => { ev.preventDefault(); seekToClientX(ev.clientX); };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
    };
    window.addEventListener("pointermove", move, { passive: false });
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  };

  // MediaRecorder webm/opus blobs usually report duration = Infinity until the media is seeked to
  // the end (a long-standing Chromium quirk), so it read as 0:00. Force the browser to resolve the
  // real length by seeking way past the end once, then snapping back to the start.
  const resolveDuration = (a) => {
    if (Number.isFinite(a.duration) && a.duration > 0) { setDur(a.duration); return; }
    const onSeeked = () => {
      a.removeEventListener("seeked", onSeeked);
      if (Number.isFinite(a.duration)) setDur(a.duration);
      a.currentTime = 0;
      setCur(0);
    };
    a.addEventListener("seeked", onSeeked);
    try { a.currentTime = 1e7; } catch { /* ignore */ }
  };

  // While playing, tick the elapsed time; when idle, show the total length.
  const shown = playing || cur > 0 ? cur : dur;

  return (
    <div className={`msg__voice ${title ? "msg__voice--file" : ""} ${playing ? "is-playing" : ""}`}>
      <button type="button" className="voice__play"
              onClick={() => { const a = ref.current; if (!a) return; if (a.paused) { ensureAnalyser(); a.play(); } else a.pause(); }}>
        <Icon name={playing ? "pause" : "play"} />
      </button>
      <div className="voice__body">
        {title && (
          <div className="voice__title">
            <span title={title}>{title}</span>
            {downloadHref && <a href={downloadHref} target="_blank" rel="noreferrer noopener" download={title}>Download</a>}
          </div>
        )}
        <div className="voice__wave" ref={waveRef} onPointerDown={onScrub} title="Tap or drag to seek">
          {Array.from({ length: BARS }).map((_, i) => (
            <i key={i} ref={(el) => { barsRef.current[i] = el; }}
               className={dur > 0 && (i + 0.5) / BARS <= cur / dur ? "played" : ""}
               style={{ height: `${barH(i)}px` }} />
          ))}
        </div>
        <div className="voice__time">{durLabel(shown)}{dur > 0 && (playing || cur > 0) ? ` / ${durLabel(dur)}` : ""}</div>
      </div>
      <audio ref={ref} src={localSrc || src} preload="metadata" style={{ display: "none" }}
             onError={() => { if (localSrc && !analyserRef.current) setViaUs(false); }}
             onLoadedMetadata={(e) => resolveDuration(e.target)}
             onDurationChange={(e) => { if (Number.isFinite(e.target.duration) && e.target.duration > 0) setDur(e.target.duration); }}
             onTimeUpdate={(e) => setCur(e.target.currentTime)}
             onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
             onEnded={(e) => { setPlaying(false); setCur(0); e.target.currentTime = 0; }} />
    </div>
  );
}

// Voice note = player + the WhatsApp-style "Transcribe" link. The transcript comes from the
// server on demand (Whisper, cached per message) and renders inline UNDER the clip — only on
// this user's screen; nothing is posted to the channel.
function VoiceNote({ m, mine }) {
  const [state, setState] = useState("idle"); // idle | busy | done | error
  const [text, setText] = useState("");
  const transcribe = async () => {
    setState("busy");
    try {
      const res = await MessagesApi.transcript(m.id);
      setText(res?.text || "");
      setState("done");
    } catch {
      setState("error");
    }
  };
  return (
    <div className="voicenote">
      <VoicePlayer src={m.mediaUrl} mine={mine} />
      {state !== "done" ? (
        <button type="button" className="voice__transcribe" onClick={transcribe} disabled={state === "busy"}>
          {state === "busy" ? "Transcribing…" : state === "error" ? "Transcription failed — tap to retry" : "Transcribe"}
        </button>
      ) : (
        <div className="voice__transcript">
          {text ? text : <i>No speech detected in this clip.</i>}
          <span className="voice__transcript-hint">Only you can see this</span>
        </div>
      )}
    </div>
  );
}

// @help renders this command guide (matches the Welcome-screen design) instead of
// the generic command-result card. Same command text source as the Welcome screen.
function CommandGuide({ channelKind }) {
  const { user } = useAuth();
  return (
    <div className="cmd-guide">
      <div className="cmd-guide__head">
        <span className="cmd-guide__icon">💬</span>
        <div>
          <div className="cmd-guide__title">Commands</div>
          <div className="cmd-guide__sub">only you can see this</div>
        </div>
      </div>
      <ul className="welcome__cmds">
        {visibleCommands(user?.isStaff, channelKind).map(([c, d]) => (<li key={c}><code>{c}</code><span>{d}</span></li>))}
      </ul>
    </div>
  );
}

// True when the issue/task behind an event card was deleted in TMCHub. Derived from the
// persisted deletedRefs set (seeded on channel load + updated live by the ticket-deleted
// webhook), so the "Removed" state is durable without any per-card fetch. The modal's own
// 404 handling remains the graceful fallback for anything deleted before this shipped.
const isDeletedRef = (deletedRefs, meta) =>
  !!(deletedRefs && ((meta.issueId && deletedRefs.has(`issue:${meta.issueId}`)) || (meta.taskId && deletedRefs.has(`task:${meta.taskId}`))));

// Reply toolbar for an event card — mirrors the message hover toolbar (top-right, on hover).
// Reply tags this ticket in the next message. Hidden on touch (@media hover:none); on mobile
// the card opens the detail modal, which carries its own "Reply in chat" action.
function CardReplyToolbar({ onReply }) {
  return (
    <div className="msg__toolbar card__toolbar">
      <button type="button" className="msg__tool" title="Reply — tag this in a message"
              onClick={(e) => { e.stopPropagation(); onReply(); }}><Icon name="reply" /></button>
    </div>
  );
}

// Bug/feature/story card (@bug/@feature/@story). Flips to a dimmed "Removed" state if its issue was deleted.
function TicketCard({ m, meta, onCardOpen, onTagTicket, deletedRefs }) {
  const removed = isDeletedRef(deletedRefs, meta);
  const type = ticketType(meta);
  if (removed) return (
    <div className={`ticket-card ticket-card--${type} ticket-card--removed`}>
      <div className="ticket-card__icon">🗑️</div>
      <div className="ticket-card__body">
        <div className="card-row card-row--top">
          <strong className="ticket-card__title">{meta.title}</strong>
          <span className="card-tag card-tag--removed">Removed</span>
        </div>
        <div className="card-row card-row--bottom">
          <div className="ticket-card__meta">
            {meta.id ? <span className="ticket-card__id">#{meta.id}</span> : null}
            deleted in TMCHub
          </div>
          <span className="card-time">{timeLabel(m.createdAt)}</span>
        </div>
      </div>
    </div>
  );
  return (
    <div className="event-row">
      <div className={`ticket-card ticket-card--${type}`} role="button" tabIndex={0} onClick={() => onCardOpen?.(meta)}>
        <span className="card-scan" aria-hidden />
        <div className="ticket-card__icon"><Icon name={type} /></div>
        <div className="ticket-card__body">
          <div className="card-row card-row--top">
            <strong className="ticket-card__title">{meta.title}</strong>
            <span className="card-tag">
              {ticketLabel(type)}
              {meta.id ? <span className="card-tag__id"> · #{meta.id}</span> : null}
            </span>
          </div>
          <div className="card-row card-row--bottom">
            <div className="ticket-card__meta">{ticketByline(type)} <b>{meta.by}</b></div>
            <span className="card-time">{timeLabel(m.createdAt)}</span>
          </div>
        </div>
      </div>
      {onTagTicket && <CardReplyToolbar onReply={() => onTagTicket(meta)} />}
    </div>
  );
}

// Task status-change card. Flips to a dimmed "Removed" state if its task was deleted.
function StatusCard({ m, meta, onCardOpen, onTagTicket, deletedRefs }) {
  const removed = isDeletedRef(deletedRefs, meta);
  if (removed) return (
    <div className="status-card status-card--removed">
      <div className="status-card__icon">🗑️</div>
      <div className="status-card__body">
        <div className="card-row card-row--top">
          <strong className="status-card__title">{meta.title}</strong>
          <span className="card-tag card-tag--removed">Removed</span>
        </div>
        <div className="card-row card-row--bottom">
          <div className="status-card__meta">deleted in TMCHub</div>
          <span className="card-time">{timeLabel(m.createdAt)}</span>
        </div>
      </div>
    </div>
  );
  const v = statusChip(meta.status);
  return (
    <div className="event-row">
      <div className="status-card" role="button" tabIndex={0} onClick={() => onCardOpen?.({ ...meta, at: m.createdAt })}>
        <span className="card-scan" aria-hidden />
        <div className="status-card__icon">{isCyber() ? <NeuralCore size={21} /> : <Icon name="refresh" />}</div>
        <div className="status-card__body">
          <div className="card-row card-row--top">
            <strong className="status-card__title">{meta.title}</strong>
            <span className={`status-chip status-chip--${v}`}>{meta.status}</span>
          </div>
          <div className="card-row card-row--bottom">
            <div className="status-card__meta">
              {/* A status change is about who moved it — show the changer, not the ticket's
                  (often auto-created "System") creator. */}
              {meta.by ? <>updated by <b>{meta.by}</b></> : (meta.reporter ? <>reported by <b>{meta.reporter}</b></> : null)}
            </div>
            <span className="card-time">{timeLabel(m.createdAt)}</span>
          </div>
        </div>
      </div>
      {onTagTicket && <CardReplyToolbar onReply={() => onTagTicket(meta)} />}
    </div>
  );
}

// Discord-style message actions.
const QUICK_REACT = ["👍", "❤️", "😂"];       // hover toolbar
const MENU_REACT = ["👍", "❤️", "😂", "🎉", "🔥"]; // context menu / sheet
// Per-user variant (a–e) → drives both the avatar colour and the sender-name
// colour via .avatar--x / .msg__sender--x classes (themable; neon under cyberpunk).
const AV = ["a", "b", "c", "d", "e"];
const avVariant = (id) => AV[Math.abs(Number(id) || 0) % AV.length];

// A single chat-message row: reply reference + bubble + reactions + hover toolbar + context
// menu + inline edit. Desktop toolbar reveals on hover (CSS); mobile uses long-press → sheet.
function ChatMessage({ m, mine, grouped, avatarUrl, mentionNames, nameById = {}, currentUserId, discussionVariant, deletedRefs, onCardOpen, isLast, flashed, menuOpen, editing, editText, setEditText,
  onOpenMenu, onCloseMenu, onOpenSheet, onImageClick, onToggleReaction, onMoreEmoji, onReply, onForward, onCopy,
  onStartEdit, onSaveEdit, onCancelEdit, onDelete, onFlashRef, onChannelOpen, onPin, onUnpin, canUnpin,
  onOpenThread, canModerate = false, saved = null, onSave, onUnsave, onRemind, awayUntil = null,
  canSelect, selectionMode, selected, onToggleSelect, onStartSelect }) {
  const pressTimer = useRef(null);
  // "Who reacted" — resolve the reactor ids to names (self first, as "You"); shown on hover
  // (desktop) and long-press (mobile, pinned into whoOpen).
  const [whoOpen, setWhoOpen] = useState(null); // emoji whose reactor list is pinned open
  const reactPress = useRef(null);
  const reactSuppress = useRef(false);
  const reactorNames = (r) => {
    const ids = r.userIds || [];
    if (ids.length === 0) return `${r.count} ${r.count === 1 ? "person" : "people"}`;
    return ids
      .map((id) => (id === currentUserId ? "You" : (nameById[id] || "Someone")))
      .sort((a, b) => (a === "You" ? -1 : b === "You" ? 1 : 0))
      .join(", ");
  };
  const startReactPress = (e, emoji) => {
    if (e.pointerType !== "touch") return;
    e.stopPropagation(); // don't also trigger the message row's long-press sheet
    reactSuppress.current = false;
    reactPress.current = setTimeout(() => { reactSuppress.current = true; setWhoOpen(emoji); }, 400);
  };
  const endReactPress = () => clearTimeout(reactPress.current);
  // Dismiss a pinned reactor list on the next tap anywhere (deferred so the long-press's own
  // pointerup doesn't close it immediately).
  useEffect(() => {
    if (whoOpen == null) return;
    const close = () => setWhoOpen(null);
    const t = setTimeout(() => document.addEventListener("pointerdown", close, { once: true }), 0);
    return () => { clearTimeout(t); document.removeEventListener("pointerdown", close); };
  }, [whoOpen]);
  // Tickets tagged in this message (snapshotted into metadata) → reference chips above the bubble.
  let msgMeta = {};
  try { msgMeta = JSON.parse(m.metadataJson || "{}"); } catch { /* ignore */ }
  const ticketRefs = Array.isArray(msgMeta.ticketRefs) ? msgMeta.ticketRefs : [];
  const channelRefs = Array.isArray(msgMeta.channelRefs) ? msgMeta.channelRefs : [];
  const forwarded = msgMeta.forwarded && typeof msgMeta.forwarded === "object" ? msgMeta.forwarded : null;
  // Plain text + shared media can be forwarded; cards/system notices can't.
  const canForward = !m.isDeleted && [0, 1, 2, 3].includes(m.messageType);
  const startPress = (e) => {
    if (e.pointerType !== "touch" || m.isDeleted) return;
    pressTimer.current = setTimeout(() => onOpenSheet(m), 450);
  };
  const endPress = () => clearTimeout(pressTimer.current);
  const reactions = m.reactions || [];

  const selectable = selectionMode && !m.isDeleted;
  return (
    <div className={`msg ${grouped ? "msg--grouped" : ""} ${menuOpen ? "msg--menuopen" : ""} ${flashed ? "msg--flash" : ""} ${selectionMode ? "msg--selecting" : ""} ${selected ? "msg--selected" : ""}`}
         data-mid={m.id}
         onClick={selectable ? () => onToggleSelect(m.id) : undefined}
         onContextMenu={(e) => { if (!selectionMode && !m.isDeleted && !editing) { e.preventDefault(); onOpenMenu(m); } }}>
      {selectionMode && (
        <span className="msg__check" aria-hidden>
          {!m.isDeleted && <span className={`msg__check-box ${selected ? "is-on" : ""}`}>{selected ? "✓" : ""}</span>}
        </span>
      )}
      {forwarded && (
        <button type="button" className="msg__fwd"
          onClick={() => onChannelOpen?.(forwarded.fromChannelId)}
          title={`Forwarded from #${forwarded.fromChannelName}`}>
          <Icon name="forward" className="ic--sm" />
          Forwarded from <b>#{forwarded.fromChannelName}</b>{forwarded.senderName ? ` · ${forwarded.senderName}` : ""}
        </button>
      )}
      {m.replyTo && (
        <>
          <span className="msg__replyref-elbow" aria-hidden />
          <button type="button" className="msg__replyref" onClick={() => onFlashRef(m.replyTo.id)} title="Go to message">
            <span className={`msg__replyref-av avatar--${avVariant(m.replyTo.senderUserId)}`}>
              {(m.replyTo.senderName || "?").trim()[0]?.toUpperCase()}
            </span>
            <span className={`msg__replyref-name msg__sender--${avVariant(m.replyTo.senderUserId)}`}>{m.replyTo.senderName}</span>
            <span className="msg__replyref-snip">{m.replyTo.deleted ? "Original message was deleted" : m.replyTo.snippet}</span>
          </button>
        </>
      )}
      {ticketRefs.length > 0 && (
        <div className="msg__ticketrefs">
          {ticketRefs.map((r) => {
            const removed = isDeletedRef(deletedRefs, r);
            const rt = ticketType(r);
            return (
              <button type="button" key={r.id}
                className={`msg__ticketref msg__ticketref--${rt} ${removed ? "is-removed" : ""}`}
                title={removed ? "Deleted in TMCHub" : `Open #${r.id}`}
                onClick={() => !removed && onCardOpen?.({ kind: "ticket", issueId: r.issueId, taskId: r.taskId, id: r.id, title: r.name, isBug: r.isBug, type: rt })}>
                <span className="msg__ticketref-ic"><Icon name={rt} className="ic--sm" /></span>
                <span className="msg__ticketref-id">#{r.id}</span>
                <span className="msg__ticketref-name">{removed ? "Deleted in TMCHub" : r.name}</span>
              </button>
            );
          })}
        </div>
      )}
      {/* Channel refs now render INLINE in the message text (like an @mention). Only fall back to the
          pinned chip for a ref whose "#name" isn't in the body — i.e. older messages sent before the
          inline change (their "#" token was stripped from the text at send time). */}
      {channelRefs.filter((r) => !(m.content || "").includes(`#${r.name}`)).length > 0 && (
        <div className="msg__ticketrefs">
          {channelRefs.filter((r) => !(m.content || "").includes(`#${r.name}`)).map((r) => (
            <button type="button" key={`ch-${r.id}`} className="msg__ticketref msg__ticketref--channel"
              title={`Go to ${r.name}`} onClick={() => onChannelOpen?.(r.id)}>
              <span className="msg__ticketref-ic">#</span>
              <span className="msg__ticketref-name">{r.name}</span>
            </button>
          ))}
        </div>
      )}
      <div className="msg__row">
        <div className="msg__avatar-col">
          {!grouped && <Avatar userId={m.senderUserId} name={m.senderName} url={avatarUrl} size="sm" className="msg__avatar" />}
        </div>
        <div className="msg__col">
        <div className="msg__bubble" onPointerDown={startPress} onPointerUp={endPress} onPointerLeave={endPress} onPointerCancel={endPress}>
          {!grouped && (
            <div className="msg__head">
              <span className={`msg__sender ${discussionVariant ? "" : `msg__sender--${avVariant(m.senderUserId)}`}`}>{m.senderName}</span>
              {awayUntil && <span className="msg__away" title={`On leave until ${leaveLabel(awayUntil)}`}>🌴</span>}
              <span className="msg__time">{timeLabel(m.createdAt)}</span>
            </div>
          )}
          {editing ? (
            <div className="msg__edit">
              <textarea className="msg__edit-input" value={editText} autoFocus rows={2}
                onChange={(e) => setEditText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSaveEdit(m); }
                  else if (e.key === "Escape") { e.preventDefault(); onCancelEdit(); }
                }} />
              <div className="msg__edit-row">
                <button type="button" className="btn btn--primary btn--sm" onClick={() => onSaveEdit(m)}>Save</button>
                <button type="button" className="btn btn--ghost btn--sm" onClick={onCancelEdit}>Cancel</button>
                <span className="msg__edit-hint">Enter to save · Esc to cancel</span>
              </div>
            </div>
          ) : m.isDeleted ? (
            <div className="msg__deleted">🚫 This message was deleted</div>
          ) : m.messageType === T.Audio && m.mediaUrl ? (
            <VoiceNote m={m} mine={mine} />
          ) : m.messageType === T.Image && m.mediaUrl ? (
            <>
              <img src={m.mediaUrl} alt="" className="msg__image" onClick={() => onImageClick?.(m.mediaUrl)} />
              {m.content && <div className="msg__text msg__caption">{renderWithMentions(m.content, mentionNames, channelRefs, onChannelOpen)}</div>}
            </>
          ) : m.messageType === T.Video && m.mediaUrl ? (
            <>
              <video src={m.mediaUrl} className="msg__video" controls preload="metadata" />
              {m.content && <div className="msg__text msg__caption">{renderWithMentions(m.content, mentionNames, channelRefs, onChannelOpen)}</div>}
            </>
          ) : m.messageType === T.File && m.mediaUrl && isAudioName(m.content) ? (
            <VoicePlayer src={m.mediaUrl} mine={mine} title={(m.content || "").trim()}
                         downloadHref={`${m.mediaUrl}${m.mediaUrl.includes("?") ? "&" : "?"}name=${encodeURIComponent((m.content || "").trim())}`} />
          ) : m.messageType === T.File && m.mediaUrl ? (
            <FileCard url={m.mediaUrl} name={m.content} />
          ) : (
            m.content && <div className="msg__text">{renderWithMentions(m.content, mentionNames, channelRefs, onChannelOpen)}</div>
          )}
          {(m.isEdited || m.pinnedAt || saved) && !m.isDeleted && (
            <div className="msg__meta">
              {saved && <span className="msg__pinned" title={saved.remindAt ? "Reminder set" : "Saved"}>{saved.remindAt ? `⏰ ${whenLabel(saved.remindAt)}` : "🔖 saved"}</span>}
              {m.pinnedAt && <span className="msg__pinned" title="Pinned in this channel">📌 pinned</span>}
              {m.isEdited && <span className="msg__edited">edited</span>}
            </div>
          )}
        </div>

        {!m.isDeleted && embeddableUrls(m.content).map((u) => <LinkEmbed key={u} url={u} />)}

        {reactions.length > 0 && (
          <div className="reactions">
            {reactions.map((r) => (
              <button type="button" key={r.emoji}
                      className={`reaction ${r.mine ? "reaction--mine" : ""} ${whoOpen === r.emoji ? "reaction--who" : ""}`}
                      onPointerDown={(e) => startReactPress(e, r.emoji)}
                      onPointerUp={endReactPress} onPointerLeave={endReactPress} onPointerCancel={endReactPress}
                      onClick={() => { if (reactSuppress.current) { reactSuppress.current = false; return; } onToggleReaction(m, r.emoji); }}>
                <span className="reaction__emoji">{r.emoji}</span>
                <span className="reaction__count">{r.count}</span>
                <span className="reaction__who-tip" role="tooltip">{reactorNames(r)}</span>
              </button>
            ))}
          </div>
        )}

        {!m.isDeleted && m.threadReplyCount > 0 && onOpenThread && (
          <button type="button" className="msg__thread" onClick={() => onOpenThread(m)}>
            💬 {m.threadReplyCount} {m.threadReplyCount === 1 ? "reply" : "replies"}
            {m.threadLastAt && <span className="msg__thread-last"> · last {timeLabel(m.threadLastAt)}</span>}
          </button>
        )}

        {!m.isDeleted && !editing && !selectionMode && (
          <div className="msg__toolbar">
            {QUICK_REACT.map((e) => (
              <button type="button" key={e} className="msg__tool msg__tool--emoji" title={`React ${e}`} onClick={() => onToggleReaction(m, e)}>{e}</button>
            ))}
            <span className="msg__tool-div" />
            {onReply && <button type="button" className="msg__tool" title="Reply" onClick={() => onReply(m)}><Icon name="reply" /></button>}
            {onOpenThread && <button type="button" className="msg__tool msg__tool--thread" title="Reply in thread" onClick={() => onOpenThread(m)}>💬</button>}
            <button type="button" className={`msg__tool ${menuOpen ? "is-active" : ""}`} title="More"
                    onClick={() => (menuOpen ? onCloseMenu() : onOpenMenu(m))}><Icon name="more" /></button>
          </div>
        )}

        {menuOpen && (
          <>
            <div className="msg__menu-overlay" onClick={onCloseMenu} onContextMenu={(e) => { e.preventDefault(); onCloseMenu(); }} />
            <div className={`msg__menu ${isLast ? "msg__menu--up" : ""}`} role="menu">
              <div className="msg__menu-emojis">
                {MENU_REACT.map((e) => (
                  <button type="button" key={e} onClick={() => { onToggleReaction(m, e); onCloseMenu(); }}>{e}</button>
                ))}
                <button type="button" className="msg__menu-moreemoji" title="More emoji" onClick={() => { onMoreEmoji?.(m); onCloseMenu(); }}><Icon name="smile" /></button>
              </div>
              {onReply && <button type="button" className="msg__menu-item" onClick={() => { onReply(m); onCloseMenu(); }}><Icon name="reply" />Reply</button>}
              {onOpenThread && <button type="button" className="msg__menu-item" onClick={() => { onOpenThread(m); onCloseMenu(); }}><span className="msg__menu-emoji">💬</span>Reply in thread</button>}
              {canForward && (
                <button type="button" className="msg__menu-item" onClick={() => { onForward(m); onCloseMenu(); }}><Icon name="forward" />Forward</button>
              )}
              <button type="button" className="msg__menu-item" onClick={() => { onCopy(m); onCloseMenu(); }}><Icon name="copy" />Copy text</button>
              {onSave && !m.isDeleted && (saved
                ? <button type="button" className="msg__menu-item" onClick={() => { onUnsave(m); onCloseMenu(); }}><span className="msg__menu-emoji">🔖</span>Remove from saved</button>
                : <button type="button" className="msg__menu-item" onClick={() => { onSave(m); onCloseMenu(); }}><span className="msg__menu-emoji">🔖</span>Save message</button>)}
              {onRemind && !m.isDeleted && (
                <div className="msg__menu-remind">
                  <span className="msg__menu-remind-label"><span className="msg__menu-emoji">⏰</span>Remind me</span>
                  <div className="msg__menu-remind-opts">
                    <button type="button" onClick={() => { onRemind(m, inOneHour()); onCloseMenu(); }}>In 1 hour</button>
                    <button type="button" onClick={() => { onRemind(m, tomorrowAt(9)); onCloseMenu(); }}>Tomorrow 9:00</button>
                    <button type="button" onClick={() => { onRemind(m, nextMondayAt(9)); onCloseMenu(); }}>Monday 9:00</button>
                  </div>
                </div>
              )}
              {onPin && !m.isDeleted && !m.pinnedAt && (
                <button type="button" className="msg__menu-item" onClick={() => { onPin(m); onCloseMenu(); }}><span className="msg__menu-emoji">📌</span>Pin message</button>
              )}
              {onUnpin && m.pinnedAt && canUnpin && (
                <button type="button" className="msg__menu-item" onClick={() => { onUnpin(m); onCloseMenu(); }}><span className="msg__menu-emoji">📌</span>Unpin for everyone</button>
              )}
              {canSelect && (
                <button type="button" className="msg__menu-item" onClick={() => { onStartSelect(m); onCloseMenu(); }}><Icon name="check-square" />Select</button>
              )}
              {mine && (
                <>
                  <div className="msg__menu-div" />
                  <button type="button" className="msg__menu-item" onClick={() => { onStartEdit(m); onCloseMenu(); }}><Icon name="edit" />Edit</button>
                  <button type="button" className="msg__menu-item msg__menu-item--danger" onClick={() => { onDelete(m); onCloseMenu(); }}><Icon name="trash" />Delete</button>
                </>
              )}
              {canModerate && onDelete && (
                <>
                  <div className="msg__menu-div" />
                  <button type="button" className="msg__menu-item msg__menu-item--danger" onClick={() => { onDelete(m); onCloseMenu(); }}><Icon name="trash" />Delete (moderate)</button>
                </>
              )}
            </div>
          </>
        )}
        </div>
      </div>
    </div>
  );
}

export default function MessageList({ messages, currentUserId, channelId, mentionables = [], onDelete, onEditSave, onImageClick, onCardOpen, onOpenPdf, onReportOpen, onTagTicket, deletedRefs, onToggleReaction, onReply, onForward, discussionVariant, onLoadOlder, hasMore = false, loadingOlder = false, emptyHint,
  canSelect = false, selectionMode = false, selectedIds, onToggleSelect, onStartSelect, hideStatusUpdates = false, channelKind = null, onPortfolioDrill, onChannelOpen, onOpenBrief, onReviseBrief, onSummarizeAgain, onJoinMeeting, onCreateStories, onIdeaOpen, editSignal = null, jumpToId = null, onJumpDone, onPin, onUnpin, isStaff = false,
  onOpenThread, newMarkerId = null, savedMap = null, onSave, onUnsave, onRemind, onClientRecap, awayById = null }) {
  const endRef = useRef(null);
  const listRef = useRef(null);
  const prevScrollHeightRef = useRef(0);
  const firstIdRef = useRef(null);
  const jumpAttemptsRef = useRef(0);
  const jumpingRef = useRef(false); // true while paging history toward a jump target — silences the
                                    // bottom-repin + scroll-triggered older-loads so they don't fight it
  const pinnedIdRef = useRef(null); // after landing a jump, hold this message dead-centre while
                                    // surrounding media/cards finish loading (else it drifts up/down)
  const pinRafRef = useRef(0);
  const mentionNames = ["all", ...mentionables.map((m) => m.name)]; // "all" → highlight the @all everyone-mention
  const avatarById = Object.fromEntries(mentionables.map((m) => [m.id, m.avatarUrl]));
  const nameById = Object.fromEntries(mentionables.map((m) => [m.id, m.name])); // reactor names for the "who reacted" tooltip

  // --- message-action UI state (one menu/sheet open at a time) ---
  const [openMenuId, setOpenMenuId] = useState(null);
  const [sheetFor, setSheetFor] = useState(null);   // message object for the mobile sheet
  const [reactPickerFor, setReactPickerFor] = useState(null); // message the full-emoji reaction picker is open for
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [flashId, setFlashId] = useState(null);
  const [pendingJump, setPendingJump] = useState(null); // reply target id we're paging history to reach
  const [jumpLoading, setJumpLoading] = useState(false); // mask the pane while paging to a jump target
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);
  const flashTimerRef = useRef(null);

  const showToast = useCallback((text) => {
    setToast(text);
    clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 1800);
  }, []);
  // Set scrollTop so the message with this id sits dead-centre in the viewport. Returns false if the
  // row isn't in the DOM. The row is wrapped in a display:contents anchor (no box), so measure/scroll
  // its real child — for a plain message that's the .msg row.
  const centerNode = useCallback((id, behavior = "auto") => {
    const el = listRef.current;
    const wrap = el?.querySelector(`[data-mid="${id}"]`);
    const node = wrap?.firstElementChild || wrap;
    if (!el || !node) return false;
    const cRect = el.getBoundingClientRect();
    const nRect = node.getBoundingClientRect();
    const top = Math.max(0, el.scrollTop + (nRect.top - cRect.top) - (el.clientHeight - nRect.height) / 2);
    if (behavior === "smooth") el.scrollTo({ top, behavior: "smooth" });
    else el.scrollTop = top;
    return true;
  }, []);

  // Hold the target dead-centre for a short window after a jump lands: chat media/cards render with
  // no reserved height, so they pop in and resize the list, drifting the highlighted message. Re-centre
  // ONLY when the list's scrollHeight actually changes (a layout shift) — so the user can still scroll
  // freely — and bail early if they interact.
  const pinCenter = useCallback((id, ms = 2000) => {
    const el = listRef.current;
    if (!el) return;
    cancelAnimationFrame(pinRafRef.current);
    pinnedIdRef.current = id;
    let lastH = el.scrollHeight;
    const startedAt = performance.now();
    const stop = () => { pinnedIdRef.current = null; cancelAnimationFrame(pinRafRef.current);
      el.removeEventListener("wheel", stop); el.removeEventListener("touchstart", stop); };
    el.addEventListener("wheel", stop, { passive: true });
    el.addEventListener("touchstart", stop, { passive: true });
    const tick = () => {
      if (pinnedIdRef.current !== id) return;
      const e = listRef.current;
      if (!e) { pinnedIdRef.current = null; return; }
      if (e.scrollHeight !== lastH) { lastH = e.scrollHeight; centerNode(id); } // layout shifted → recentre
      if (performance.now() - startedAt < ms) pinRafRef.current = requestAnimationFrame(tick);
      else stop();
    };
    pinRafRef.current = requestAnimationFrame(tick);
  }, [centerNode]);

  // Scroll to a message that's currently in the DOM and flash it. Returns false if not rendered.
  const scrollToLoaded = useCallback((id, smooth = true) => {
    const el = listRef.current;
    const wrap = el?.querySelector(`[data-mid="${id}"]`);
    if (!el || !wrap) return false;
    const node = wrap.firstElementChild || wrap;
    if (!centerNode(id, smooth ? "smooth" : "auto")) return false;
    // Highlight: plain messages use the bubble .msg--flash (via flashId); cards (no .msg) get the
    // row-level jump-flash ring. Only one applies per row, so no double outline.
    setFlashId(id);
    if (!node.classList.contains("msg")) node.classList.add("jump-flash");
    clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => { setFlashId(null); node.classList.remove("jump-flash"); }, 3200);
    return true;
  }, [centerNode]);
  // Jump to a replied-to message. If it isn't loaded (older than the loaded window), start paging
  // history toward it — the effect below loads older pages until it appears, then scrolls to it.
  const flashRef = useCallback((id) => {
    if (scrollToLoaded(id, false)) { pinCenter(id); return; } // loaded → centre, hold still, done
    jumpAttemptsRef.current = 0;
    jumpingRef.current = true;        // paging toward it — stand down the competing scroll effects
    setJumpLoading(true);            // mask the pane so the catch-up paging isn't seen thrashing
    setPendingJump(id);
  }, [scrollToLoaded, pinCenter]);

  // Jump to a specific message when the channel is opened from a search result (or deep link):
  // wait for the first page to land, then reuse flashRef (which pages older history if the target
  // isn't in the loaded window). Fires once per target.
  const jumpDoneRef = useRef(false);
  useEffect(() => {
    // Reset once the parent clears the request (jumpToId→null) so the SAME target can be jumped to
    // again later even without a channel remount.
    if (jumpToId == null) { jumpDoneRef.current = false; return; }
    if (jumpDoneRef.current || !messages.length) return; // wait for the first page, fire once
    jumpDoneRef.current = true;
    flashRef(jumpToId);
    onJumpDone?.();
  }, [jumpToId, messages, flashRef, onJumpDone]);

  // Drive the "page back until the reply target is loaded" jump. Runs whenever the message list or
  // load state changes while a jump is pending: scroll once it's in, otherwise fetch the next older
  // page. Bounded by hasMore and an attempt cap so it can't loop forever.
  useEffect(() => {
    if (pendingJump == null) return;
    const id = pendingJump;
    // Detect the target in the DOM (the same string data-mid match the manual click uses — the
    // source of truth for "can we scroll to it"), NOT messages.some(id===) which was unreliable.
    const inDom = !!listRef.current?.querySelector(`[data-mid="${id}"]`);
    if (inDom) {
      setPendingJump(null);
      // Scroll INSTANTLY (a smooth scroll gets cancelled by the layout still settling after the
      // prepend — the "loads but doesn't jump" symptom). Retry a few frames until it's laid out,
      // then release the jump lock so the normal bottom-follow / load-older behaviour resumes.
      // Scroll instantly while still behind the mask, then drop the mask to reveal the message
      // already in place + flashing — one smooth reveal instead of a visible scroll.
      let tries = 0;
      const land = () => {
        if (scrollToLoaded(id, false)) {
          jumpingRef.current = false; setJumpLoading(false);
          pinCenter(id); // hold it dead-centre while the revealed media/cards finish loading
          return;
        }
        if (tries++ < 12) requestAnimationFrame(land);
        else { jumpingRef.current = false; setJumpLoading(false); }
      };
      requestAnimationFrame(land);
      return;
    }
    if (loadingOlder) return; // a page is in flight — wait for it
    if (hasMore && onLoadOlder && jumpAttemptsRef.current < 60) {
      jumpAttemptsRef.current += 1;
      const el = listRef.current;
      if (el) prevScrollHeightRef.current = el.scrollHeight; // keep the viewport anchored across catch-up loads
      onLoadOlder();
    } else {
      setPendingJump(null); // reached the top (or the cap) without finding it
      jumpingRef.current = false;
      setJumpLoading(false);
      showToast("Couldn't find the original message");
    }
  }, [pendingJump, messages, hasMore, loadingOlder, onLoadOlder, scrollToLoaded, pinCenter, showToast]);
  const copyText = useCallback(async (m) => {
    try { await navigator.clipboard.writeText(m.content || ""); showToast("Copied to clipboard"); }
    catch { showToast("Couldn't copy"); }
  }, [showToast]);
  const startEdit = useCallback((m) => { setEditingId(m.id); setEditText(m.content || ""); }, []);
  const saveEdit = useCallback((m) => {
    const t = (editText || "").trim();
    if (t && t !== m.content) onEditSave?.(m.id, t);
    setEditingId(null); setEditText("");
  }, [editText, onEditSave]);
  const cancelEdit = useCallback(() => { setEditingId(null); setEditText(""); }, []);
  // Open the forward picker (the modal + its own confirmation is the feedback, no toast here).
  const forwardMsg = useCallback((m) => { onForward?.(m); }, [onForward]);

  // External "edit my last message" request (composer ArrowUp on an empty box).
  useEffect(() => {
    if (!editSignal?.id) return;
    const m = messages.find((x) => x.id === editSignal.id);
    if (m) { startEdit(m); requestAnimationFrame(() => scrollToLoaded(m.id, false)); }
  }, [editSignal?.nonce]); // eslint-disable-line react-hooks/exhaustive-deps

  // Esc closes an open menu / sheet.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { setOpenMenuId(null); setSheetFor(null); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Keep the view pinned to the bottom while late-loading media grows the content. Chat GIFs/
  // images have no reserved height, so they render at 0px, the initial scroll-to-bottom lands
  // against a short layout, then each media element loads (~200px) and pushes the last messages
  // below the fold. `load`/`loadeddata` don't bubble but fire in the capture phase, so one
  // listener on the container catches every descendant image/video — re-pin only while the
  // viewer is actually at the bottom (atBottomRef), so it never yanks someone reading history.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const repin = () => {
      if (jumpingRef.current) return;
      // While a jump is pinned, a loading image resizing the list would drift the target — re-centre
      // it instead of following the bottom.
      if (pinnedIdRef.current != null) { centerNode(pinnedIdRef.current); return; }
      if (atBottomRef.current) el.scrollTop = el.scrollHeight;
    };
    el.addEventListener("load", repin, true);
    el.addEventListener("loadeddata", repin, true);
    return () => { el.removeEventListener("load", repin, true); el.removeEventListener("loadeddata", repin, true); };
  }, [centerNode]);

  // Scroll management. Three cases, keyed off the first/last message ids:
  //  • first load of a channel  → jump to the bottom
  //  • new message at the bottom → follow only if already near the bottom
  //  • older page prepended (top id changed, bottom unchanged) → keep the viewport
  //    anchored so the user stays where they were reading.
  const lastIdRef = useRef(null);
  const atBottomRef = useRef(true); // was the viewer at the bottom before the last update?
  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const firstId = messages.length ? messages[0].id : null;
    const lastId = messages.length ? messages[messages.length - 1].id : null;
    const prevFirst = firstIdRef.current, prevLast = lastIdRef.current;
    firstIdRef.current = firstId;
    lastIdRef.current = lastId;

    // A jump is holding the target centred — don't let bottom-follow / anchor logic move the view.
    if (pinnedIdRef.current != null) return;

    if (prevFirst != null && firstId !== prevFirst && lastId === prevLast) {
      el.scrollTop = el.scrollTop + el.scrollHeight - prevScrollHeightRef.current; // anchor after prepend
    } else if (prevLast == null) {
      el.scrollTop = el.scrollHeight; // first load → bottom
      // Re-pin after paint: fonts / ticket-ref chips / async avatars can grow the list a
      // touch after this pass, otherwise leaving the last message clipped by the composer.
      requestAnimationFrame(() => { const e2 = listRef.current; if (e2) e2.scrollTop = e2.scrollHeight; });
    } else if (lastId !== prevLast) {
      // Follow a new message if the viewer was at the bottom BEFORE it arrived — measured
      // via atBottomRef (a post-append measure wrongly skips tall incoming messages).
      if (atBottomRef.current) endRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Stay pinned to the bottom while the content above settles. The history lands at the bottom, but images,
  // videos and link previews finish loading over the next second or two and — with no space reserved — push
  // everything down while the scroll position stays put, leaving the viewer thousands of px up ("loads, jumps
  // up, comes back down"). Rows are display:contents wrappers (no box to ResizeObserve), so listen instead for
  // anything finishing loading inside the list (img load / video metadata, captured) and for nodes being
  // inserted (embeds rendering). Only while the viewer is at the bottom, and never during a jump.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return undefined;
    // Pinned synchronously (load events and MutationObserver callbacks run before the next paint), so the
    // displaced frame is never drawn — deferring to requestAnimationFrame still flashed one frame off-bottom.
    const pin = () => {
      if (pinnedIdRef.current != null || jumpingRef.current) return;
      if (atBottomRef.current && el.scrollHeight - el.scrollTop - el.clientHeight > 1) el.scrollTop = el.scrollHeight;
    };
    el.addEventListener("load", pin, true);
    el.addEventListener("loadedmetadata", pin, true);
    const mo = new MutationObserver(pin);
    mo.observe(el, { childList: true, subtree: true });
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(pin) : null;
    ro?.observe(el); // the list itself resizing (composer grows, panels open) — keep the latest message in view
    return () => {
      el.removeEventListener("load", pin, true);
      el.removeEventListener("loadedmetadata", pin, true);
      mo.disconnect();
      ro?.disconnect();
    };
  }, []);

  // Hover toolbar (react / reply / thread / ⋯) sits right after the END of the hovered message's own content —
  // text, image or card — instead of pinned to the far right of the row, where on a wide screen it was nowhere
  // near a short message and floated over the one above (people reacted to the wrong message). Measured on
  // hover; clamped so it never runs off the row; the ⋯ menu opens from the same spot.
  const toolbarRowRef = useRef(null);
  const placeToolbar = useCallback((e) => {
    const row = e.target.closest?.(".msg, .event-row");
    if (!row || row === toolbarRowRef.current) return;
    toolbarRowRef.current = row;
    const tb = row.querySelector(".msg__toolbar");
    if (!tb) return;
    const rowRect = row.getBoundingClientRect();
    let contentRight = null;
    if (row.classList.contains("msg")) {
      // Where the message's VISIBLE content ends: text runs (measured per text node — a range over the block
      // itself would report the full width of its boxes) plus media / previews / files / voice / reactions.
      const col = row.querySelector(".msg__col");
      if (col) {
        let max = -Infinity;
        const range = document.createRange();
        const walker = document.createTreeWalker(col, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, {
          acceptNode: (n) => (n.nodeType === 1 && n.matches(".msg__toolbar, .msg__menu, .msg__menu-overlay")
            ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
        });
        for (let n = walker.nextNode(); n; n = walker.nextNode()) {
          let b = null;
          if (n.nodeType === 3) { if (!n.textContent.trim()) continue; range.selectNodeContents(n); b = range.getBoundingClientRect(); }
          else if (n.matches("img, video, audio, iframe, .lembed, .msg__file, .msg__voice, .voice, .reaction")) b = n.getBoundingClientRect();
          if (b && b.width) max = Math.max(max, b.right);
        }
        if (max > -Infinity) contentRight = max;
      }
    } else {
      const card = [...row.children].find((c) => !c.classList.contains("msg__toolbar"));
      contentRight = card?.getBoundingClientRect().right ?? null;
    }
    if (contentRight == null || !rowRect.width) return;
    const tbW = tb.offsetWidth || 220;
    const left = Math.max(56, Math.min(contentRight - rowRect.left + 10, rowRect.width - tbW - 10));
    const menuLeft = Math.max(8, Math.min(left, rowRect.width - 240));
    row.style.setProperty("--tb-left", `${Math.round(left)}px`);
    row.style.setProperty("--tb-right", "auto");
    row.style.setProperty("--menu-left", `${Math.round(menuLeft)}px`);
  }, []);

  function handleScroll(e) {
    const el = e.currentTarget;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    // Don't page older history while the user is actively selecting text: dragging a selection
    // toward the top auto-scrolls the pane, which would fire load-older → prepend + scroll-anchor,
    // and that DOM/scroll jump collapses the native selection ("only highlights one line", the view
    // lurches back). Let them finish selecting; loading resumes on the next scroll with no selection.
    const sel = typeof window !== "undefined" ? window.getSelection() : null;
    const selecting = !!(sel && sel.rangeCount > 0 && !sel.isCollapsed && el.contains(sel.anchorNode));
    // While a jump is paging history, the jump loop owns older-loading — don't also trigger it here
    // (the target scrolls near the top mid-jump, which would otherwise fire a competing load → bounce).
    if (!selecting && !jumpingRef.current && pinnedIdRef.current == null && el.scrollTop <= 80 && hasMore && !loadingOlder && onLoadOlder) {
      prevScrollHeightRef.current = el.scrollHeight; // capture before the prepend
      onLoadOlder();
    }
  }

  return (
    <div className="messages-wrap">
    <div className={`messages ${discussionVariant ? `messages--${discussionVariant}` : ""}`} data-tour="messages" ref={listRef} onScroll={handleScroll} onMouseOver={placeToolbar}>
      {loadingOlder && <div className="messages__older"><span className="btn__spin" /></div>}
      {messages.length === 0 && (emptyHint ?? <Welcome hint={false} />)}
      {(hideStatusUpdates ? messages.filter((m) => !isStatusMsg(m)) : messages).flatMap((m, i, arr) => {
        const prev = arr[i - 1];
        const dayDiv = (!prev || dayKey(prev.createdAt) !== dayKey(m.createdAt))
          ? (
            <div className="day-divider" key={`day-${m.id}`}>
              <i className="day-divider__gem" aria-hidden />
              <span className="day-divider__line day-divider__line--l" aria-hidden />
              <span className="day-divider__label"><i className="day-divider__slash" aria-hidden>//</i>{dayLabel(m.createdAt)}</span>
              <span className="day-divider__line day-divider__line--r" aria-hidden />
              <i className="day-divider__gem" aria-hidden />
            </div>
          )
          : null;
        // "New messages" line above the first message that was unread when the channel opened.
        const divider = m.id === newMarkerId
          ? [dayDiv, <div className="new-divider" key={`new-${m.id}`}><span>New messages</span></div>]
          : dayDiv;

        // Render the row body (may be a card or a plain message), then wrap it in a display:contents
        // anchor carrying data-mid so search/reply jumps can locate ANY row — not just plain messages.
        const out = (() => {
        if (m.messageType === T.Signoff)
          return [divider, <SignoffCard key={m.id} message={m} currentUserId={currentUserId} onOpenPdf={onOpenPdf} />];

        // TMCHub posts these (sender 0), so staff remove a stray one with the card's ✕.
        if ((m.messageType === T.Deliverable || m.messageType === T.Storyboard) && m.isDeleted) return [divider];

        if (m.messageType === T.Deliverable)
          return [divider, <DeliverableCard key={m.id} message={m} onDelete={isStaff ? onDelete : null} />];

        if (m.messageType === T.Storyboard)
          return [divider, <StoryboardCard key={m.id} message={m} onDelete={isStaff ? onDelete : null} />];

        if (m.messageType === T.Meeting)
          return [divider, <MeetingCard key={m.id} message={m} onJoin={onJoinMeeting} onDelete={onDelete} currentUserId={currentUserId} />];

        if (m.messageType === T.System) {
          let meta = {};
          try { meta = JSON.parse(m.metadataJson || "{}"); } catch { /* ignore */ }

          if (meta.kind === "status") {
            // Spooky theme: a fully-animated hero scene card (unless the task was deleted → keep
            // the plain "Removed" state). Every other theme keeps the compact StatusCard.
            if (isSpooky() && !isDeletedRef(deletedRefs, meta))
              return [divider, <div key={m.id} className="event-row"><SpookyEventCard kind="status" m={m} meta={meta} onCardOpen={onCardOpen} />{onTagTicket && <CardReplyToolbar onReply={() => onTagTicket(meta)} />}</div>];
            if (isDeep() && !isDeletedRef(deletedRefs, meta))
              return [divider, <div key={m.id} className="event-row"><DeepSpaceEventCard kind="status" m={m} meta={meta} onCardOpen={onCardOpen} />{onTagTicket && <CardReplyToolbar onReply={() => onTagTicket(meta)} />}</div>];
            return [divider, <StatusCard key={m.id} m={m} meta={meta} onCardOpen={onCardOpen} onTagTicket={onTagTicket} deletedRefs={deletedRefs} />];
          }

          if (meta.kind === "ticket") {
            if (isSpooky() && !isDeletedRef(deletedRefs, meta))
              return [divider, <div key={m.id} className="event-row"><SpookyEventCard kind={ticketType(meta)} m={m} meta={meta} onCardOpen={onCardOpen} />{onTagTicket && <CardReplyToolbar onReply={() => onTagTicket(meta)} />}</div>];
            if (isDeep() && !isDeletedRef(deletedRefs, meta))
              return [divider, <div key={m.id} className="event-row"><DeepSpaceEventCard kind={ticketType(meta)} m={m} meta={meta} onCardOpen={onCardOpen} />{onTagTicket && <CardReplyToolbar onReply={() => onTagTicket(meta)} />}</div>];
            return [divider, <TicketCard key={m.id} m={m} meta={meta}
              onCardOpen={onCardOpen} onTagTicket={onTagTicket} deletedRefs={deletedRefs} />];
          }

          if (meta.kind === "broadcast") {
            return [divider, (
              <div key={m.id} className="broadcast-card">
                <div className="broadcast-card__head">
                  <span className="broadcast-card__icon">📢</span>
                  <div className="broadcast-card__ht">
                    <strong className="broadcast-card__title">Announcement</strong>
                    <span className="broadcast-card__sub">from {meta.by || "TMC"}</span>
                  </div>
                  <span className="card-time">{timeLabel(m.createdAt)}</span>
                </div>
                <div className="broadcast-card__body">{m.content}</div>
              </div>
            )];
          }

          if (meta.kind === "build") {
            // Same hover-arrow affordance as the bug/feature/status cards: tag the build card into a
            // message as a reply. Tapping that reply jumps back to this card (loading older history if
            // needed) via the standard reply-jump path.
            return [divider, (
              <div key={m.id} className="event-row">
                <BuildCard m={m} meta={meta} onCardOpen={onCardOpen} />
                {onReply && <CardReplyToolbar onReply={() => onReply(m)} />}
              </div>
            )];
          }

          if (m.isDeleted) return [divider]; // a notice staff removed — nothing to show, not even a tombstone
          return [divider, (
            <div key={m.id} className="msg-system-row">
              <div className="msg-system" dangerouslySetInnerHTML={{ __html: mdLite(m.content) }} />
              {isStaff && onDelete && m.senderUserId === 0 && (
                <button type="button" className="msg-system__del" title="Remove this notice for everyone" onClick={() => onDelete(m)}>✕</button>
              )}
            </div>
          )];
        }

        if (m.messageType === T.CommandResult) {
          // A deleted card (e.g. an /adreport the requester removed) shows the same tombstone as a
          // deleted message — the server has already stripped its metadata.
          if (m.isDeleted)
            return [divider, <div key={m.id} className="cmd-card cmd-card--deleted"><div className="msg__deleted">🚫 This message was deleted</div></div>];
          let cmeta = {};
          try { cmeta = JSON.parse(m.metadataJson || "{}"); } catch { /* ignore */ }
          if (cmeta.kind === "help") return [divider, <CommandGuide key={m.id} channelKind={channelKind} />];
          // ---- Reporting channel: Adscope marketing report (public card + its loading/error states) ----
          if (cmeta.kind === "adreport")
            return [divider, <AdReportCard key={m.id} m={m} cmeta={cmeta} currentUserId={currentUserId} onDelete={onDelete} time={timeLabel(m.createdAt)} />];
          if (cmeta.kind === "adreport-loading") {
            return [divider, (
              <div key={m.id} className="summary-card summary-card--loading">
                <div className="summary-card__head">
                  <span className="summary-card__icon summary-card__spin">📈</span>
                  <div className="summary-card__ht">
                    <strong className="summary-card__title">Pulling the {cmeta.company || "Affinity"} report…</strong>
                    <span className="summary-card__sub">{cmeta.label || "last 7 days"} · Adscope campaign data + insights</span>
                  </div>
                </div>
                <div className="summary-card__skel"><span /><span /><span /></div>
              </div>
            )];
          }
          if (cmeta.kind === "adreport-error") {
            return [divider, (
              <div key={m.id} className="cmd-card">
                <div className="cmd-card__head">
                  <span>📈</span><span className="grow">Report</span>
                  <span className="t">{timeLabel(m.createdAt)}</span>
                </div>
                <div className="cmd-card__sub">⚠️ {cmeta.message || m.content}</div>
              </div>
            )];
          }
          if (cmeta.kind === "poll") return [divider, <PollCard key={m.id} m={m} currentUserId={currentUserId} />];
          if (cmeta.kind === "idea")
            return [divider, <IdeaCard key={m.id} m={m} currentUserId={currentUserId} onOpen={onIdeaOpen} />];
          if (cmeta.kind === "summary-loading") {
            if (isSpooky())
              return [divider, <SummaryCard key={m.id} phase="loading" period={cmeta.period} channelName={cmeta.title} />];
            if (isDeep())
              return [divider, <DeepSpaceSummaryCard key={m.id} phase="loading" period={cmeta.period} channelName={cmeta.title} />];
            return [divider, (
              <div key={m.id} className="summary-card summary-card--loading">
                <div className="summary-card__head">
                  <span className="summary-card__icon summary-card__spin">🤖</span>
                  <div className="summary-card__ht">
                    <strong className="summary-card__title">Summarising the chat…</strong>
                    <span className="summary-card__sub">last {cmeta.period} · reading the conversation</span>
                  </div>
                </div>
                <div className="summary-card__skel"><span /><span /><span /></div>
              </div>
            )];
          }
          if (cmeta.kind === "summary-error") {
            if (isSpooky())
              return [divider, <SummaryCard key={m.id} phase="error" period={cmeta.period} channelName={cmeta.title} />];
            if (isDeep())
              return [divider, <DeepSpaceSummaryCard key={m.id} phase="error" period={cmeta.period} channelName={cmeta.title} />];
            return [divider, (
              <div key={m.id} className="summary-card">
                <div className="summary-card__head">
                  <span className="summary-card__icon">⚠️</span>
                  <div className="summary-card__ht">
                    <strong className="summary-card__title">Summary took too long</strong>
                    <span className="summary-card__sub">please run /summarize again</span>
                  </div>
                </div>
              </div>
            )];
          }
          if (cmeta.kind === "summary") {
            if (isSpooky())
              return [divider, (
                <SummaryCard key={m.id} phase="done" period={cmeta.period} channelName={cmeta.title}
                             time={timeLabel(m.createdAt)} onRerun={onSummarizeAgain}>
                  <SummaryBody text={m.content} />
                </SummaryCard>
              )];
            if (isDeep())
              return [divider, (
                <DeepSpaceSummaryCard key={m.id} phase="done" period={cmeta.period} channelName={cmeta.title}
                             time={timeLabel(m.createdAt)} onRerun={onSummarizeAgain}>
                  <SummaryBody text={m.content} />
                </DeepSpaceSummaryCard>
              )];
            return [divider, (
              <div key={m.id} className="summary-card">
                <div className="summary-card__head">
                  <span className="summary-card__icon">📝</span>
                  <div className="summary-card__ht">
                    <strong className="summary-card__title">Chat summary — {cmeta.title}</strong>
                    <span className="summary-card__sub">last {cmeta.period} · only you can see this</span>
                  </div>
                  <span className="card-time">{timeLabel(m.createdAt)}</span>
                </div>
                <div className="summary-card__body"><SummaryBody text={m.content} /></div>
              </div>
            )];
          }
          // /ask — private answer from the channel's own history (loading → answer | error, morphed
          // in place by Channels.onReceive). Staff get the meeting-notes "Create stories…" dialog for the
          // proposals the answer carries (cmeta.actions).
          if (cmeta.kind === "question-loading") {
            return [divider, (
              <div key={m.id} className="summary-card summary-card--loading">
                <div className="summary-card__head">
                  <span className="summary-card__icon summary-card__spin">🤖</span>
                  <div className="summary-card__ht">
                    <strong className="summary-card__title">{cmeta.question}</strong>
                    <span className="summary-card__sub">reading the conversation…</span>
                  </div>
                </div>
                <div className="summary-card__skel"><span /><span /><span /></div>
              </div>
            )];
          }
          if (cmeta.kind === "question-error") {
            return [divider, (
              <div key={m.id} className="summary-card">
                <div className="summary-card__head">
                  <span className="summary-card__icon">⚠️</span>
                  <div className="summary-card__ht">
                    <strong className="summary-card__title">{cmeta.question}</strong>
                    <span className="summary-card__sub">{cmeta.text || "Couldn't answer that — please ask again"}</span>
                  </div>
                </div>
              </div>
            )];
          }
          if (cmeta.kind === "question") {
            return [divider, (
              <div key={m.id} className="summary-card">
                <div className="summary-card__head">
                  <span className="summary-card__icon">{cmeta.found === false ? "🤷" : "💬"}</span>
                  <div className="summary-card__ht">
                    <strong className="summary-card__title">{cmeta.question}</strong>
                    <span className="summary-card__sub">{cmeta.title} · last {cmeta.days || 90} days · only you can see this</span>
                  </div>
                  <span className="card-time">{timeLabel(m.createdAt)}</span>
                </div>
                <div className="summary-card__body"><SummaryBody text={m.content} /></div>
                {onCreateStories && Array.isArray(cmeta.actions) && cmeta.actions.some((a) => !a.storyId) && (
                  <div className="summary-card__actionsbar">
                    <button type="button" className="btn btn--soft" onClick={() => onCreateStories(m)}>
                      📋 Create stories…
                    </button>
                  </div>
                )}
              </div>
            )];
          }
          if (cmeta.kind === "meetingnotes") {
            // pending = the server posted this right when the meeting ended and is still
            // transcribing + summarizing; it edits this same message into the notes when done.
            return [divider, (
              <div key={m.id} className="summary-card">
                <div className="summary-card__head">
                  <span className="summary-card__icon">📝</span>
                  <div className="summary-card__ht">
                    <strong className="summary-card__title">Meeting notes — {cmeta.title}</strong>
                    <span className="summary-card__sub">
                      {cmeta.pending ? "Transcribing the meeting…" : "AI-generated from the meeting transcript"}
                    </span>
                  </div>
                  <span className="card-time">{timeLabel(m.createdAt)}</span>
                </div>
                <div className="summary-card__body">
                  {cmeta.pending
                    ? (cmeta.etaSeconds > 0
                        ? <NotesProgress etaSeconds={cmeta.etaSeconds} startedAt={cmeta.startedAt ?? m.createdAt} />
                        : <div className="summary-card__pending">Generating meeting notes — this takes a minute or two…</div>)
                    : <SummaryBody text={m.content} />}
                </div>
                {!cmeta.pending && (onClientRecap || (onCreateStories && Array.isArray(cmeta.actions) && cmeta.actions.some((a) => !a.storyId))) && (
                  <div className="summary-card__actionsbar">
                    {onCreateStories && Array.isArray(cmeta.actions) && cmeta.actions.some((a) => !a.storyId) && (
                      <button type="button" className="btn btn--soft" onClick={() => onCreateStories(m)}>
                        📋 Create stories…
                      </button>
                    )}
                    {onClientRecap && (
                      <button type="button" className="btn btn--soft" onClick={() => onClientRecap(m)}>
                        ✉️ Client recap…
                      </button>
                    )}
                  </div>
                )}
              </div>
            )];
          }
          if (cmeta.kind === "weekly-roundup") {
            const chans = Array.isArray(cmeta.channels) ? cmeta.channels : [];
            return [divider, (
              <div key={m.id} className="summary-card roundup-card">
                <div className="summary-card__head">
                  <span className="summary-card__icon">📅</span>
                  <div className="summary-card__ht">
                    <strong className="summary-card__title">Weekly roundup · {cmeta.range}</strong>
                    <span className="summary-card__sub">{chans.length} active channels · staff only</span>
                  </div>
                  <span className="card-time">{timeLabel(m.createdAt)}</span>
                </div>
                <div className="summary-card__body">
                  {chans.map((c) => (
                    <details key={c.channelId} className="roundup-card__chan">
                      <summary>
                        <span className="roundup-card__name">{c.name}</span>
                        <span className="roundup-card__count">{c.messages} messages</span>
                        {onChannelOpen && <button type="button" className="roundup-card__open" onClick={(e) => { e.preventDefault(); onChannelOpen(c.channelId); }}>Open ↗</button>}
                      </summary>
                      <SummaryBody text={c.summary} />
                    </details>
                  ))}
                </div>
              </div>
            )];
          }
          if (cmeta.kind === "client-recap") {
            return [divider, (
              <div key={m.id} className="summary-card">
                <div className="summary-card__head">
                  <span className="summary-card__icon">✉️</span>
                  <div className="summary-card__ht">
                    <strong className="summary-card__title">Meeting recap — {cmeta.title}</strong>
                    <span className="summary-card__sub">from {cmeta.by || "TMC"}</span>
                  </div>
                  <span className="card-time">{timeLabel(m.createdAt)}</span>
                </div>
                <div className="summary-card__body"><SummaryBody text={m.content} /></div>
              </div>
            )];
          }
          if (cmeta.kind === "build") {
            return [divider, <BuildCard key={m.id} m={m} meta={cmeta} onCardOpen={onCardOpen} />];
          }
          if (cmeta.kind === "ai-plan") {
            const tickets = Array.isArray(cmeta.tickets) ? cmeta.tickets : [];
            return [divider, (
              <div key={m.id} className="aiplan-card">
                <div className="aiplan-card__head">
                  <span className="aiplan-card__icon">🤖</span>
                  <strong className="aiplan-card__title">Feature plan</strong>
                  <span className="card-time">{timeLabel(m.createdAt)}</span>
                </div>
                {cmeta.insufficientInfo ? (
                  <div className="aiplan-card__needs">
                    <div className="aiplan-card__needs-label">Needs more info</div>
                    {cmeta.missing ? <p className="aiplan-card__missing">{cmeta.missing}</p> : null}
                  </div>
                ) : (
                  <>
                    {cmeta.summary ? <p className="aiplan-card__summary">{cmeta.summary}</p> : null}
                    {tickets.length > 0 ? (
                      <div className="aiplan-card__tickets">
                        {tickets.map((t) => (
                          <button
                            key={t.id}
                            type="button"
                            className="aiplan-card__ticket"
                            onClick={() => onCardOpen?.({ kind: "ticket", taskId: t.id, id: t.id, title: t.title })}
                          >
                            <span className="aiplan-card__ticket-id">#{t.id}</span>
                            <span className="aiplan-card__ticket-title">{t.title}</span>
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            )];
          }
          if (cmeta.kind === "report") {
            const v = statusChip(cmeta.health);
            return [divider, (
              <div key={m.id} className="report-card" data-tour="reportcard" role="button" tabIndex={0} onClick={() => onReportOpen?.(cmeta)}>
                <div className="report-card__icon">📊</div>
                <div className="report-card__body">
                  <div className="card-row card-row--top">
                    <strong className="report-card__title">{cmeta.title || "Report"}</strong>
                    {cmeta.health ? <span className={`status-chip status-chip--${v}`}>{cmeta.health}</span> : null}
                  </div>
                  <div className="report-card__stats">
                    <span>✅ <b>{cmeta.completed ?? 0}</b> completed</span>
                    <span>🔧 <b>{cmeta.inProgress ?? 0}</b> in progress</span>
                    <span>🎫 <b>{cmeta.ticketsResolved ?? 0}</b> resolved · <b>{cmeta.openTickets ?? 0}</b> open</span>
                  </div>
                  {cmeta.summary ? <p className="report-card__summary">{cmeta.summary}</p> : null}
                  <div className="report-card__foot">
                    <span className="report-card__cta">{cmeta.url ? "Tap to open the full report" : "Summary only"}</span>
                    <span className="card-time">{timeLabel(m.createdAt)}</span>
                  </div>
                </div>
              </div>
            )];
          }
          if (cmeta.kind === "campaign-brief") {
            const brief = { briefId: cmeta.briefId, title: cmeta.title, version: cmeta.version };
            return [divider, (
              <div key={m.id} className="report-card campaign-card" role="button" tabIndex={0} onClick={() => onOpenBrief?.(brief)}>
                <div className="report-card__icon">📋</div>
                <div className="report-card__body">
                  <div className="card-row card-row--top">
                    <strong className="report-card__title">{cmeta.title || "Campaign brief"}</strong>
                    {cmeta.version ? <span className="status-chip status-chip--info">v{cmeta.version}</span> : null}
                  </div>
                  <p className="report-card__summary">Marketing campaign brief — tap to view the PDF.</p>
                  <div className="report-card__foot">
                    <span className="report-card__cta">Tap to open the brief PDF</span>
                    <span className="card-time">{timeLabel(m.createdAt)}</span>
                  </div>
                </div>
                {onReviseBrief ? (
                  <button
                    type="button" className="campaign-card__revise"
                    onClick={(e) => { e.stopPropagation(); onReviseBrief(brief); }}
                  >Revise</button>
                ) : null}
              </div>
            )];
          }
          // ---- Dream Team ops cards (private, cross-product) ----
          if (cmeta.kind === "portfolio") {
            const raw = Array.isArray(cmeta.rows) ? cmeta.rows : [];
            // Attention-first: clients with overdue/blocked float up, then by active projects, then name.
            const rows = [...raw].sort((a, b) =>
              ((b.overdue * 10 + b.blocked) - (a.overdue * 10 + a.blocked)) ||
              ((b.activeProjects || 0) - (a.activeProjects || 0)) ||
              String(a.client).localeCompare(String(b.client)));
            const tot = raw.reduce((s, r) => ({
              proj: s.proj + (r.projects || 0), act: s.act + (r.activeProjects || 0),
              feat: s.feat + (r.features || 0), blk: s.blk + (r.blocked || 0), ovd: s.ovd + (r.overdue || 0),
            }), { proj: 0, act: 0, feat: 0, blk: 0, ovd: 0 });
            const zero = (n) => n > 0 ? n : <span className="dt-zero">·</span>;
            const drill = (r, metric) => onPortfolioDrill?.({ title: r.client, clientId: r.clientId, metric, days: 30 });
            const cell = (r, metric) => {
              const n = metric === "blocked" ? r.blocked : r.overdue;
              const cls = metric === "blocked" ? "warn" : "bad";
              if (n > 0 && r.clientId != null)
                return <button type="button" className={`dt-cellbtn dt-cellbtn--${cls}`} onClick={() => drill(r, metric)}>{n}</button>;
              return <span className={n > 0 ? `dt-num dt-num--${cls}` : ""}>{zero(n)}</span>;
            };
            return [divider, (
              <div key={m.id} className="dt-card dt-card--pf">
                <div className="dt-card__head">
                  <span className="dt-card__icon">📊</span>
                  <div className="dt-card__ht">
                    <strong className="dt-card__title">Portfolio overview</strong>
                    <span className="dt-card__sub">{cmeta.window ? `${cmeta.window} · ` : ""}{cmeta.clients ?? raw.length} clients</span>
                  </div>
                  <span className="card-time">{timeLabel(m.createdAt)}</span>
                </div>
                <div className="pf-sum">
                  <div className="pf-sum__s"><b>{tot.act}/{tot.proj}</b><span>active proj</span></div>
                  <div className="pf-sum__s"><b>{tot.feat}</b><span>features</span></div>
                  <div className={`pf-sum__s${tot.blk > 0 ? " pf-sum__s--warn" : ""}`}><b>{tot.blk}</b><span>blocked</span></div>
                  <div className={`pf-sum__s${tot.ovd > 0 ? " pf-sum__s--bad" : ""}`}><b>{tot.ovd}</b><span>overdue</span></div>
                </div>
                <div className="dt-table dt-table--pf" role="table">
                  <div className="dt-table__row dt-table__row--head" role="row">
                    <span className="dt-table__c dt-table__c--name">Client</span>
                    <span className="dt-table__c dt-table__c--proj">Projects</span>
                    <span className="dt-table__c">Feat</span>
                    <span className="dt-table__c">Blk</span>
                    <span className="dt-table__c">Ovd</span>
                  </div>
                  {rows.map((r, i) => {
                    const health = r.overdue > 0 ? "bad" : r.blocked > 0 ? "warn" : "ok";
                    const pct = r.projects > 0 ? Math.round((r.activeProjects / r.projects) * 100) : 0;
                    return (
                      <div className="dt-table__row" role="row" key={i}>
                        <span className="dt-table__c dt-table__c--name">
                          <span className={`pf-dot pf-dot--${health}`} aria-hidden />{r.client}
                        </span>
                        <span className="dt-table__c dt-table__c--proj">
                          <span className="pf-bar"><span className="pf-bar__fill" style={{ width: `${pct}%` }} /></span>
                          <span className="pf-bar__lbl">{r.activeProjects}/{r.projects}</span>
                        </span>
                        <span className="dt-table__c">{zero(r.features)}</span>
                        <span className="dt-table__c">{cell(r, "blocked")}</span>
                        <span className="dt-table__c">{cell(r, "overdue")}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )];
          }
          if (cmeta.kind === "projectstatus") {
            const fmtDate = (d) => { if (!d) return null; try { return new Date(d).toLocaleDateString(); } catch { return null; } };
            const dl = fmtDate(cmeta.deadline);
            // A stat tile — tappable (opens the task drill-down) when it has a count and we know the project.
            const tile = (count, label, metric, extra = "") => {
              const n = count ?? 0;
              const cls = `dt-stat${extra ? ` ${extra}` : ""}`;
              return n > 0 && cmeta.projectId
                ? <button type="button" className={`${cls} dt-stat--tap`} onClick={() => onPortfolioDrill?.({ title: cmeta.title, projectId: cmeta.projectId, metric })}><b>{n}</b><span>{label} ›</span></button>
                : <div className={cls}><b>{n}</b><span>{label}</span></div>;
            };
            return [divider, (
              <div key={m.id} className="dt-card">
                <div className="dt-card__head">
                  <span className="dt-card__icon">📈</span>
                  <div className="dt-card__ht">
                    <strong className="dt-card__title">{cmeta.title}</strong>
                    <span className="dt-card__sub">{cmeta.client || "—"}{dl ? ` · due ${dl}` : ""}</span>
                  </div>
                  <span className="card-time">{timeLabel(m.createdAt)}</span>
                </div>
                <div className="dt-stats">
                  {tile(cmeta.active, "active", "active")}
                  {tile(cmeta.completed, "done", "completed")}
                  {tile(cmeta.blocked, "blocked", "blocked", cmeta.blocked > 0 ? "dt-stat--warn" : "")}
                  {tile(cmeta.overdue, "overdue", "overdue", cmeta.overdue > 0 ? "dt-stat--bad" : "")}
                  <div className="dt-stat"><b>{cmeta.features ?? 0}</b><span>features</span></div>
                  <div className="dt-stat"><b>{Math.round(cmeta.workedHours ?? 0)}/{Math.round(cmeta.estimatedHours ?? 0)}</b><span>hrs</span></div>
                </div>
              </div>
            )];
          }
          if (cmeta.kind === "aispend") {
            const rows = Array.isArray(cmeta.rows) ? cmeta.rows : [];
            const max = rows.reduce((mx, r) => Math.max(mx, r.usd || 0), 0) || 1;
            return [divider, (
              <div key={m.id} className="dt-card">
                <div className="dt-card__head">
                  <span className="dt-card__icon">🤖</span>
                  <div className="dt-card__ht">
                    <strong className="dt-card__title">AI spend · last {cmeta.period}</strong>
                    <span className="dt-card__sub">${(cmeta.totalUsd ?? 0).toFixed(2)}{cmeta.totalZar > 0 ? ` · R${Math.round(cmeta.totalZar)}` : ""} total</span>
                  </div>
                  <span className="card-time">{timeLabel(m.createdAt)}</span>
                </div>
                <div className="dt-bars">
                  {rows.length === 0 ? <p className="dt-card__empty">No AI spend in this window.</p> : rows.map((r, i) => (
                    <div className="dt-bar" key={i}>
                      <span className="dt-bar__label">{r.label}</span>
                      <span className="dt-bar__track"><span className="dt-bar__fill" style={{ width: `${Math.max(4, Math.round((r.usd / max) * 100))}%` }} /></span>
                      <span className="dt-bar__val">${(r.usd ?? 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )];
          }
          if (cmeta.kind === "mywork") {
            const rows = Array.isArray(cmeta.rows) ? cmeta.rows : [];
            const fmtDate = (d) => { if (!d) return null; try { return new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short" }); } catch { return null; } };
            return [divider, (
              <div key={m.id} className="dt-card">
                <div className="dt-card__head">
                  <span className="dt-card__icon">🗂</span>
                  <div className="dt-card__ht">
                    <strong className="dt-card__title">Your work</strong>
                    <span className="dt-card__sub">{cmeta.count ?? rows.length} open across products</span>
                  </div>
                  <span className="card-time">{timeLabel(m.createdAt)}</span>
                </div>
                <ul className="dt-work">
                  {rows.map((r) => {
                    const due = fmtDate(r.dueDate);
                    return (
                      <li className="dt-work__item" key={r.id}>
                        <button type="button" className="dt-work__btn" onClick={() => onCardOpen?.({ kind: "ticket", taskId: r.id, id: r.id, title: r.name, isBug: r.isBug })}>
                          <span className={`dt-work__tag ${r.isBug ? "dt-work__tag--bug" : "dt-work__tag--feat"}`}>{r.isBug ? "Bug" : "Task"}</span>
                          <span className="dt-work__name">{r.name}</span>
                          <span className="dt-work__meta">{r.product || ""}{due ? ` · ${due}` : ""} · {r.status}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )];
          }
          return [divider, (
            <div key={m.id} className="cmd-card">
              <div className="cmd-card__head">
                <span>🤖</span><span className="grow">Result</span>
                <span className="t">{timeLabel(m.createdAt)}</span>
              </div>
              <div className="cmd-card__sub" dangerouslySetInnerHTML={{ __html: mdLite(m.content) }} />
            </div>
          )];
        }

        // Coerce both sides to string: the live SignalR payload and the REST history can type the
        // id differently (number vs string), which broke a strict === and hid Edit/Delete on a
        // freshly-sent message until a reload rehydrated it.
        const mine = String(m.senderUserId) === String(currentUserId);
        // Group consecutive chat messages from the same sender (same day, not a reply,
        // neither deleted) → hide the avatar/header, show time in the gutter on hover.
        const grouped = !divider && prev && prev.messageType <= T.Video && prev.senderUserId === m.senderUserId
          && !m.replyTo && !m.isDeleted && !prev.isDeleted;
        return [divider, (
          <ChatMessage key={m.id} m={m} mine={mine} grouped={grouped} avatarUrl={avatarById[m.senderUserId]} mentionNames={mentionNames}
            nameById={nameById} currentUserId={currentUserId}
            discussionVariant={discussionVariant} deletedRefs={deletedRefs} onCardOpen={onCardOpen}
            isLast={i === messages.length - 1}
            flashed={flashId === m.id} menuOpen={openMenuId === m.id}
            editing={editingId === m.id} editText={editText} setEditText={setEditText}
            onOpenMenu={(mm) => { setSheetFor(null); setOpenMenuId(mm.id); }}
            onCloseMenu={() => setOpenMenuId(null)}
            onOpenSheet={(mm) => { setOpenMenuId(null); setSheetFor(mm); }}
            onImageClick={onImageClick}
            onToggleReaction={onToggleReaction}
            onMoreEmoji={(mm) => setReactPickerFor(mm)}
            onReply={onReply} onForward={forwardMsg} onCopy={copyText}
            onStartEdit={startEdit} onSaveEdit={saveEdit} onCancelEdit={cancelEdit}
            onDelete={onDelete} onFlashRef={flashRef} onChannelOpen={onChannelOpen}
            onPin={onPin} onUnpin={onUnpin} canUnpin={!!m.pinnedAt && (mine || m.pinnedByUserId === currentUserId || !!isStaff)}
            onOpenThread={onOpenThread} canModerate={!!isStaff && !mine}
            saved={savedMap?.has(m.id) ? { remindAt: savedMap.get(m.id) } : null} onSave={onSave} onUnsave={onUnsave} onRemind={onRemind}
            awayUntil={awayById?.[m.senderUserId] || null}
            canSelect={canSelect} selectionMode={selectionMode} selected={!!selectedIds?.has?.(m.id)}
            onToggleSelect={onToggleSelect} onStartSelect={onStartSelect} />
        )];
        })();
        if (!out) return [];
        const parts = Array.isArray(out) ? out : [out];
        const contentEls = parts.slice(1).filter(Boolean); // [0] is the day divider, rest is the body
        if (!contentEls.length) return divider ? [divider] : [];
        return [divider, (
          <div key={m.id} data-mid={m.id} className="msg-anchor" style={{ display: "contents" }}>{contentEls}</div>
        )];
      })}
      <div ref={endRef} />

      {sheetFor && (
        <div className="ma-sheet-wrap" role="dialog" aria-modal="true">
          <div className="ma-backdrop" onClick={() => setSheetFor(null)} />
          <div className="ma-sheet">
            <div className="ma-sheet__grabber" />
            <div className="ma-sheet__emojis">
              {MENU_REACT.map((e) => (
                <button type="button" key={e} onClick={() => { onToggleReaction?.(sheetFor, e); setSheetFor(null); }}>{e}</button>
              ))}
              <button type="button" className="ma-sheet__moreemoji" onClick={() => { setReactPickerFor(sheetFor); setSheetFor(null); }}><Icon name="smile" /></button>
            </div>
            <div className="ma-sheet__list">
              <button type="button" onClick={() => { onReply?.(sheetFor); setSheetFor(null); }}><Icon name="reply" />Reply</button>
              {!sheetFor.isDeleted && [0, 1, 2, 3].includes(sheetFor.messageType) && (
                <button type="button" onClick={() => { forwardMsg(sheetFor); setSheetFor(null); }}><Icon name="forward" />Forward</button>
              )}
              <button type="button" onClick={() => { copyText(sheetFor); setSheetFor(null); }}><Icon name="copy" />Copy text</button>
              {canSelect && (
                <button type="button" onClick={() => { onStartSelect?.(sheetFor); setSheetFor(null); }}><Icon name="check-square" />Select</button>
              )}
              {!sheetFor.isDeleted && String(sheetFor.senderUserId) === String(currentUserId) && (
                <>
                  <button type="button" onClick={() => { startEdit(sheetFor); setSheetFor(null); }}><Icon name="edit" />Edit</button>
                  <button type="button" className="ma-sheet__danger" onClick={() => { onDelete?.(sheetFor); setSheetFor(null); }}><Icon name="trash" />Delete</button>
                </>
              )}
            </div>
            <button type="button" className="ma-sheet__cancel" onClick={() => setSheetFor(null)}>Cancel</button>
          </div>
        </div>
      )}
      {reactPickerFor && (
        <ReactionPicker
          onPick={(emoji) => { onToggleReaction?.(reactPickerFor, emoji); setReactPickerFor(null); }}
          onClose={() => setReactPickerFor(null)} />
      )}
      {toast && <div className="ma-toast">{toast}</div>}
    </div>
      {jumpLoading && (
        <div className="messages__jumpmask" aria-live="polite">
          <span className="btn__spin" />
          <span className="messages__jumpmask-label">Finding message…</span>
        </div>
      )}
    </div>
  );
}
